import type { BaseOptionType } from "antd/lib/select"

import { FINANCE_ITEM_TYPES } from "../../../../constants/finance-item-constants.ts"
import { RESPONSIBLE_TYPES } from "../../../../constants/responsible-type-constants.ts"

// An EXPENSE line is rejected by the API unless it's a non-planned payable, so
// the Source/Target picker disables it elsewhere instead of letting the save 400.
export const EXPENSE_DISABLED_REASON = "Available only on actual payable lines."

// A Receivable belongs to a customer or a reimbursement (a sourceless group the
// summary counts); on any other contact it never reaches the financial summary,
// so block it here instead of letting the save 400.
export const RECEIVABLE_DISABLED_REASON =
  "Available only for customer or reimbursement sources."

// The mirror: a customer only owes the project, so it can never be a payable.
export const PAYABLE_DISABLED_REASON = "Not available for customer contacts."

export const isExpenseAllowed = (
  itemType: number | null | undefined,
  isPlanned: boolean | null | undefined
): boolean => itemType === FINANCE_ITEM_TYPES.PAYABLE && !isPlanned

export const isReceivableAllowed = (
  responsibleType: number | null | undefined
): boolean =>
  responsibleType == null ||
  responsibleType === RESPONSIBLE_TYPES.CUSTOMER ||
  responsibleType === RESPONSIBLE_TYPES.REIMBURSEMENT

export const isPayableAllowed = (
  responsibleType: number | null | undefined
): boolean => responsibleType !== RESPONSIBLE_TYPES.CUSTOMER

export const isResponsibleTypeAllowedForItemType = (
  responsibleType: number | null | undefined,
  itemType: number | null | undefined
): boolean => {
  if (itemType === FINANCE_ITEM_TYPES.RECEIVABLE)
    return isReceivableAllowed(responsibleType)
  if (itemType === FINANCE_ITEM_TYPES.PAYABLE)
    return isPayableAllowed(responsibleType)
  return true
}

export const buildResponsibleTypeOptions = (
  baseOptions: BaseOptionType[] | undefined,
  expenseAllowed: boolean,
  itemType?: number | null
): BaseOptionType[] =>
  (baseOptions ?? []).map((option) => {
    if (option.value === RESPONSIBLE_TYPES.EXPENSE) {
      return { ...option, disabled: !expenseAllowed }
    }
    return isResponsibleTypeAllowedForItemType(option.value, itemType)
      ? option
      : { ...option, disabled: true }
  })

export const responsibleTypeDisabledReason = (
  responsibleType: number | null | undefined,
  itemType: number | null | undefined,
  expenseAllowed: boolean
): string => {
  if (responsibleType === RESPONSIBLE_TYPES.EXPENSE && !expenseAllowed)
    return EXPENSE_DISABLED_REASON
  if (itemType === FINANCE_ITEM_TYPES.RECEIVABLE) return RECEIVABLE_DISABLED_REASON
  if (itemType === FINANCE_ITEM_TYPES.PAYABLE) return PAYABLE_DISABLED_REASON
  return EXPENSE_DISABLED_REASON
}

export const buildItemTypeOptions = (
  baseOptions: BaseOptionType[] | undefined,
  receivableAllowed: boolean,
  payableAllowed: boolean
): BaseOptionType[] =>
  (baseOptions ?? []).map((option) => {
    if (option.value === FINANCE_ITEM_TYPES.RECEIVABLE) {
      return { ...option, disabled: !receivableAllowed }
    }
    if (option.value === FINANCE_ITEM_TYPES.PAYABLE) {
      return { ...option, disabled: !payableAllowed }
    }
    return option
  })
